import { useState } from 'react';
import { MessageCircle, Reply, MoreVertical, Flag, Trash2, Edit, Check, X, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useComments, Comment, EntityType } from '@/hooks/community/useComments';
import { useCommentLikes } from '@/hooks/community/useCommentLikes';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
interface CommentsThreadProps {
  entityType: EntityType;
  entityId: string;
}
export function CommentsThread({
  entityType,
  entityId
}: CommentsThreadProps) {
  const [newComment, setNewComment] = useState('');
  const {
    comments,
    loading,
    submitting,
    sortBy,
    setSortBy,
    submitComment,
    deleteComment,
    editComment,
    reportComment
  } = useComments(entityType, entityId);
  const {
    user
  } = useAuth();
  const handleSubmit = async (content: string, parentPath?: string) => {
    const success = await submitComment(content, parentPath);
    if (success && !parentPath) {
      setNewComment('');
    }
    return success;
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, content: string, parentPath?: string) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit(content, parentPath);
    }
  };
  return <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <MessageCircle className="h-5 w-5" />
          Comments ({comments.length})
        </h3>
        
        {comments.length > 0 && <Tabs value={sortBy} onValueChange={value => setSortBy(value as 'top' | 'new')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="top">Top</TabsTrigger>
              <TabsTrigger value="new">New</TabsTrigger>
            </TabsList>
          </Tabs>}
      </div>

      {/* Comment composer */}
      <div className="space-y-3">
        <Textarea placeholder={user ? "Share your thoughts... (⌘+Enter to post)" : "Sign in to comment"} value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => handleKeyDown(e, newComment)} disabled={!user || submitting} className="resize-none" rows={3} maxLength={2000} />
        
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            {newComment.length}/2000 characters
          </div>
          
          <Button onClick={() => handleSubmit(newComment)} disabled={!user || !newComment.trim() || submitting || newComment.length > 2000} size="sm">
            {submitting ? 'Posting...' : 'Post Comment'}
          </Button>
        </div>
      </div>

      {/* Comments list */}
      <div className="space-y-4">
        {loading ? <div className="space-y-4">
            {[1, 2, 3].map(i => <CommentSkeleton key={i} />)}
          </div> : comments.length > 0 ? comments.map(comment => <CommentItem key={comment.id} comment={comment} onReply={handleSubmit} onDelete={deleteComment} onEdit={editComment} onReport={reportComment} currentUserId={user?.id} />) : <div className="text-center py-12 text-muted-foreground">
            <MessageCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h4 className="text-lg font-medium mb-2">No comments yet</h4>
            <p>Be the first to share your thoughts on this {entityType}!</p>
          </div>}
      </div>
    </div>;
}
interface CommentItemProps {
  comment: Comment;
  onReply: (content: string, parentPath?: string) => Promise<boolean>;
  onDelete: (commentId: string) => Promise<void>;
  onEdit: (commentId: string, newContent: string) => Promise<void>;
  onReport: (commentId: string, reason: string) => Promise<void>;
  currentUserId?: string;
  depth?: number;
}
function CommentItem({
  comment,
  onReply,
  onDelete,
  onEdit,
  onReport,
  currentUserId,
  depth = 0
}: CommentItemProps) {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [submitting, setSubmitting] = useState(false);
  const { likesCount, isLiked, toggleLike } = useCommentLikes(comment.id);
  const handleReply = async () => {
    setSubmitting(true);
    const success = await onReply(replyContent, comment.path);
    if (success) {
      setReplyContent('');
      setShowReplyForm(false);
    }
    setSubmitting(false);
  };
  const handleEdit = async () => {
    if (editContent.trim() === comment.content) {
      setIsEditing(false);
      return;
    }
    setSubmitting(true);
    try {
      await onEdit(comment.id, editContent.trim());
      setIsEditing(false);
    } finally {
      setSubmitting(false);
    }
  };
  const handleCancelEdit = () => {
    setEditContent(comment.content);
    setIsEditing(false);
  };
  const handleDelete = () => {
    onDelete(comment.id);
  };
  const handleReport = (reason: string) => {
    onReport(comment.id, reason);
  };
  const isOwner = currentUserId === comment.user_id;
  const canReply = depth < 5;
  return <div className={cn('space-y-3', depth > 0 && 'ml-6 border-l border-border pl-4')}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-primary">
            {(comment.user_display_name || comment.user_email)?.charAt(0).toUpperCase() || '?'}
          </span>
        </div>
        
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{comment.user_display_name || comment.user_email}</span>
            <span className="text-muted-foreground">
              {new Date(comment.created_at).toLocaleDateString()}
            </span>
          </div>
          
          <div className="prose prose-sm max-w-none">
            {comment.is_deleted ? <em className="text-muted-foreground">[Deleted by author]</em> : isEditing ? <div className="space-y-2">
                <Textarea value={editContent} onChange={e => setEditContent(e.target.value)} className="resize-none" rows={2} maxLength={2000} />
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {editContent.length}/2000 characters
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={submitting}>
                      <X className="h-3 w-3 mr-1" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleEdit} disabled={!editContent.trim() || submitting || editContent.length > 2000}>
                      <Check className="h-3 w-3 mr-1" />
                      {submitting ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              </div> : <p className="whitespace-pre-wrap">{comment.content}</p>}
          </div>

          {!comment.is_deleted && !isEditing && <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={toggleLike}
                className={cn(
                  "h-8 px-2 text-muted-foreground hover:text-foreground",
                  isLiked && "text-red-500 hover:text-red-600"
                )}
              >
                <Heart className={cn("h-3 w-3", isLiked && "fill-current")} />
                {likesCount > 0 && <span className="ml-1 text-xs">{likesCount}</span>}
              </Button>

              {canReply && <Button variant="ghost" size="sm" onClick={() => setShowReplyForm(!showReplyForm)} className="h-8 px-2 text-muted-foreground hover:text-foreground">
                  <Reply className="h-3 w-3 mr-1" />
                  Reply
                </Button>}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-muted-foreground hover:text-foreground">
                    <MoreVertical className="h-3 w-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-slate-50">
                  {isOwner && <>
                      <DropdownMenuItem onClick={() => setIsEditing(true)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </DropdownMenuItem>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <DropdownMenuItem onSelect={e => e.preventDefault()}>
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Comment</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to permanently delete this comment? This action cannot be undone and the comment will be completely removed.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                              Delete Permanently
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>}
                  
                  {!isOwner && currentUserId && <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <DropdownMenuItem onSelect={e => e.preventDefault()}>
                          <Flag className="h-4 w-4 mr-2" />
                          Report
                        </DropdownMenuItem>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Report Comment</AlertDialogTitle>
                          <AlertDialogDescription>
                            Why are you reporting this comment?
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleReport('Inappropriate content')}>
                            Report as Inappropriate
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>}

          {/* Reply form */}
          {showReplyForm && <div className="space-y-2 pt-2">
              <Textarea placeholder="Write a reply... (⌘+Enter to post)" value={replyContent} onChange={e => setReplyContent(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              handleReply();
            }
          }} className="resize-none" rows={2} maxLength={2000} />
              
              <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  {replyContent.length}/2000 characters
                </div>
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setShowReplyForm(false)}>
                    Cancel
                  </Button>
                  <Button size="sm" onClick={handleReply} disabled={!replyContent.trim() || submitting || replyContent.length > 2000}>
                    {submitting ? 'Posting...' : 'Reply'}
                  </Button>
                </div>
              </div>
            </div>}
        </div>
      </div>

          {/* Nested replies */}
          {comment.replies?.map(reply => <CommentItem key={reply.id} comment={reply} onReply={onReply} onDelete={onDelete} onEdit={onEdit} onReport={onReport} currentUserId={currentUserId} depth={depth + 1} />)}
    </div>;
}
function CommentSkeleton() {
  return <div className="flex items-start gap-3">
      <Skeleton className="w-8 h-8 rounded-full" />
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="h-16 w-full" />
        <div className="flex gap-2">
          <Skeleton className="h-6 w-12" />
          <Skeleton className="h-6 w-6" />
        </div>
      </div>
    </div>;
}